import type {
  BankCriteria,
  BillingPeriod,
  HowToKnow,
  ModalityCriteria,
  PaymentCriteria,
  Plans,
  SortingCriteria,
  TypeCriteria,
  UserWorkArea,
} from "@/types";
import { ChartConfig } from "./components/ui/chart";

export const redisRegistryKey = "registry";
export const redisAnalyzeKey = "analyze";

export const telegramChatErrorsId = -1002154946525;
export const telegramChatCheckoutId = -1002387086429;

export const formURL = "https://forms.gle/pCA2jbv7dqzczHn29";
export const emailSupport = "murillomir0@gmail.com";
export const phoneSupport = "5547989030732";
export const discountCoupon = "DESCONTO15POR3M";
export const priceApp = 39;

export const newId = "novo";

export const storageKeys = {
  onboardingProfileCompleted: "onboardingProfileCompleted",
  onboardingFeaturesCompleted: "onboardingFeaturesCompleted",
  userUf: "userUf",
  trialBannerClosed: "trial-banner-closed",
  normalBannerClosed: "normal-banner-closed",
};

export const auctionCommisionPercentage = 0.05;
export const realStateAgentPercentage = 0.05;
export const iptuPercentage = 0.01;
export const monthsToSell = 18;

export const monthsToPay = 360;

export const yearInMonths = 12;

export const defaultSortingCriteria: SortingCriteria = "dataDoLeilao";

export const defaultTypeCriteria: TypeCriteria = "todos";

export const defaultEmptyText = "Nenhum resultado encontrado.";

export const userWorkArea = {
  iniciante: { label: "Sou iniciante", value: "iniciante" },
  corretor: { label: "Sou corretor", value: "corretor" },
  investidor: { label: "Sou investidor", value: "investidor" },
  imobiliaria: { label: "Sou uma imobiliária", value: "imobiliaria" },
  outro: { label: "Outro", value: "outro" },
} satisfies Record<UserWorkArea, { label: string; value: string }>;

export const userWorkAreaOptions = [
  userWorkArea.iniciante.value,
  userWorkArea.corretor.value,
  userWorkArea.investidor.value,
  userWorkArea.imobiliaria.value,
  userWorkArea.outro.value,
] as const;

export const userHowToKnow = {
  google: { label: "Google", value: "google" },
  instagram: { label: "Instagram", value: "instagram" },
  facebook: { label: "Facebook", value: "facebook" },
  linkedin: { label: "LinkedIn", value: "linkedin" },
  outro: { label: "Outro", value: "outro" },
} satisfies Record<HowToKnow, { label: string; value: string }>;

export const userHowToKnowOptions = [
  userHowToKnow.google.value,
  userHowToKnow.instagram.value,
  userHowToKnow.facebook.value,
  userHowToKnow.linkedin.value,
  userHowToKnow.outro.value,
] as const;

export const banks = {
  caixa: {
    primaryLabel: "Caixa Econômica Federal",
    secondaryLabel: "CAIXA",
    value: "caixa",
    loanPercentage: 0.05,
    logo: "/logos/caixa-economica-federal-logo.svg",
  },
  santander: {
    primaryLabel: "Santander Brasil",
    secondaryLabel: "Santander",
    value: "santander",
    loanPercentage: 0.2,
    logo: "/logos/santander-logo.svg",
  },
  bradesco: {
    primaryLabel: "Bradesco",
    secondaryLabel: "Bradesco",
    value: "bradesco",
    loanPercentage: 0.25,
    logo: "/logos/bradesco-logo.svg",
  },
} as const;

const defaultFeatures = [
  {
    title: "Acesso a todos os imóveis",
    description:
      "Acesso completo a todos os imóveis de todas as regiões do Brasil.",
  },
  {
    title: "Favoritos ilimitados",
    description:
      "Favorite todo e qualquer tipo de imóvel para facilitar a sua busca e análise na listagem.",
  },
];

const excludedFeatures = [
  {
    title: "Alertas inteligentes",
    description:
      "Além de configurar regiões e cidades, você pode configurar o tipo do imóvel, tipo de pagamento, data de leilão entre outros.",
  },
  {
    title: "Análise de matrículas por IA",
    description:
      "Descubra informações essenciais sobre o histórico do imóvel e garanta uma análise completa para uma compra segura e estratégica.",
  },
  {
    title: "Diligência prévia",
    description:
      "Confira os links de processos, irregulariedades e outras informações importantes sobre o imóvel.",
  },
  {
    title: "Calculadoras avançadas",
    description:
      "Ferramentas e calculadoras avançadas para análise completa dos imóveis.",
  },
  {
    title: "Modelos de contrato",
    description:
      "Modelos de contrato de compra e venda de imóveis para facilitar a sua negociação.",
  },
  {
    title: "Documentos de intimação",
    description:
      "Documentos de intimação para facilitar a sua negociação com o proprietário.",
  },
  {
    title: "Planilha Excel",
    description: "Exportação de dados dos imóveis para uma planilha Excel.",
  },
  {
    title: "Navegação 100% livre de anúncios",
    description:
      "Não será exibido anúncios no site, durante a navegação não aparecerá banners de propaganda.",
  },
  {
    title: "Atendimento 24/7 via WhatsApp",
    description:
      "Tire suas dúvidas na hora com auxílio de um suporte humano via WhatsApp.",
  },
];

export const billingPeriod = {
  mensal: "mensal",
  anual: "anual",
} satisfies Record<BillingPeriod, BillingPeriod>;

export const plans = {
  especialista: {
    value: "especialista",
    icon: "/small-icon-especialista.png",
    label: "Especialista",
    description:
      "Tudo para encontrar, ser alertado e analisar ofertas rapidamente.",
    price: priceApp,
    features: [...defaultFeatures, ...excludedFeatures],
    excludedFeatures: [],
  },
  gratis: {
    value: "gratis",
    icon: "/small-icon-iniciante.png",
    label: "Iniciante",
    description: "Acesso básico para quem está começando.",
    price: 0,
    features: [
      {
        title: "Imóveis limitados",
        description:
          "Acesso limitado a todos os imóveis de todas as regiões do Brasil.",
      },
      {
        title: "Favoritos limitados",
        description: "Apenas 3 favoritos por conta.",
      },
    ],
    excludedFeatures,
  },
} satisfies Record<
  Plans,
  {
    value: Plans;
    icon: string;
    label: string;
    description: string;
    price: number;
    features: { title: string; description: string }[];
    excludedFeatures: { title: string; description: string }[];
  }
>;

export const checkoutCoupon = {
  prod: "Xa2v1bBf",
  dev: "R5caclUK",
} satisfies Record<"prod" | "dev", string>;

export const checkoutDashboardPlans = {
  prod: "https://billing.stripe.com/p/login/14k6p260FepQdYkcMM",
  dev: "https://billing.stripe.com/p/login/test_8wMg2L2xbdE2dKEaEE",
} satisfies Record<"prod" | "dev", string>;

export const checkoutPriceId = {
  prod: {
    [plans.especialista.value]: {
      [billingPeriod.mensal]: "price_1RBK5kCLf6yfZh8vql9C5pme",
      [billingPeriod.anual]: "price_1RBJhyCLf6yfZh8vh6SE8Zjo",
    },
    [plans.gratis.value]: {
      [billingPeriod.mensal]: "price_1RBK5kCLf6yfZh8vql9C5pme",
      [billingPeriod.anual]: "price_1RBJhyCLf6yfZh8vh6SE8Zjo",
    },
  },
  dev: {
    [plans.especialista.value]: {
      [billingPeriod.mensal]: "price_1Pwr3UCLf6yfZh8vYhET51Jq",
      [billingPeriod.anual]: "price_1RBJaNCLf6yfZh8vekHQKgw8",
    },
    [plans.gratis.value]: {
      [billingPeriod.mensal]: "price_1Pwr3UCLf6yfZh8vYhET51Jq",
      [billingPeriod.anual]: "price_1RBJaNCLf6yfZh8vekHQKgw8",
    },
  },
} satisfies Record<
  "prod" | "dev",
  Record<Plans, Record<BillingPeriod, string>>
>;

export const bankRoutes = {
  [banks.caixa.value]: "caixa-economica-federal",
  [banks.santander.value]: "santander",
  [banks.bradesco.value]: "bradesco",
} satisfies Record<BankCriteria, string>;

export const sortingCriteria = {
  dataDoLeilao: "Data do leilão",
  dataDeInclusao: "Data de inclusão",
  menorPreco: "Menor preço",
  maiorPreco: "Maior preço",
  maiorDesconto: "Maior desconto",
} satisfies Record<SortingCriteria, string>;

export const bankCriteria = {
  [banks.caixa.value]: banks.caixa.secondaryLabel,
  [banks.santander.value]: banks.santander.secondaryLabel,
  [banks.bradesco.value]: banks.bradesco.secondaryLabel,
} satisfies Record<BankCriteria, string>;

export const modalityCriteria = {
  "1": "Venda Direta Online",
  "2": "Venda Online",
  "3": "Licitação Aberta",
  "4": "Leilão SFI - Edital Único",
} satisfies Record<ModalityCriteria, string>;

export const modalityReverseCriteria = {
  "Venda Direta Online": "1",
  "Venda Online": "2",
  "Licitação Aberta": "3",
  "Leilão SFI - Edital Único": "4",
} satisfies Record<string, ModalityCriteria>;

export const paymentCriteria = {
  fgts: "FGTS",
  financiamento: "Financiamento",
  parcelamento: "Parcelamento",
  consorcio: "Consórcio",
} satisfies Record<PaymentCriteria, string>;

export const typeCriteria = {
  todos: "Todos os imóveis",
  casa: "Casas",
  apartamento: "Apartamentos",
  terreno: "Terrenos",
  sala: "Salas",
  comercial: "Comerciais",
  galpão: "Galpões",
  loja: "Lojas",
  predio: "Prédios",
  lote: "Lotes",
  gleba: "Glebas",
} satisfies Record<TypeCriteria, string>;

export const today = new Date();
export const todayUTC = new Date(
  Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
    0,
    0,
    0
  )
);

export const todayUnixUTC = todayUTC.getTime();
export const nowUTC = Math.floor(Date.now() / 1000);
export const nowBRTimezone = new Date(
  today.getTime() - today.getTimezoneOffset() * 60000 - 3 * 60 * 60000
);

export const minChunkSize = 5;
export const maxChunkSize = 12;

export const santanderPages = 110;

export const chartProfitConfig = {
  valor_gasto_total: {
    label: "Gasto total",
    color: "hsl(var(--chart-2))",
  },
  valor_lucro_liquido_sozinho: {
    label: "Lucro líquido sozinho",
    color: "hsl(var(--chart-1))",
  },
  valor_lucro_liquido_com_corretor: {
    label: "Lucro líquido com corretor",
    color: "hsl(var(--chart-3))",
  },
  valor_investimento_selic: {
    label: "Investindo à taxa SELIC (14,25%)",
    color: "hsl(var(--chart-4))",
  },
} satisfies ChartConfig;

export const TABS_ESTATE = {
  imovel: {
    value: "imovel",
    label: "Imóvel",
  },
  calculadora: {
    value: "calculadora",
    label: "Calculadora",
  },
  matricula: {
    value: "matricula",
    label: "Matrícula",
  },
  diligencia: {
    value: "diligencia",
    label: "Diligência prévia",
  },
  fotos: {
    value: "fotos",
    label: "Fotos",
  },
} as const;

export const areaSizes = ["25", "50", "75", "100", "150", "200", "250"];
